<template>
    <div id="twoFA-component" style="width: 50%;">
        <div v-if="!step" id="enable-2fa">
            <span class="dashboard-subtitle mb-5">Two-factor authentication</span>

            <p class="dashboard-text">
                Two factor authentication adds an additional layer of security to your account by requiring more
                than just a password to log in.
            </p>

            <p class="dashboard-text mb-10">
                Current two factor authentication status: <span
                :class="{'color-red': !is2FAEnabled, 'color-green': is2FAEnabled}"
            >{{ is2FAEnabled ? 'Enabled' : 'Not Enabled' }}</span>
            </p>

            <template v-if="!is2FAEnabled">
                <v-btn class="color-gray rounded-xl mt-auto" outlined large @click="startEnabling2FA">
                    Enable two factor authentication
                </v-btn>
            </template>

            <template v-else>
                <v-dialog
                    v-model="showQRCodeDialog"
                    content-class="account-modal"
                    max-width="450px"
                    transition="fade-transition"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" class="color-gray rounded-xl" outlined large
                               @click="createQRCode"
                        >
                            Show QR code
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title>
                            <span class="text">Two-factor authentication QR code</span>
                        </v-card-title>
                        <v-card-text>
                            <v-container>
                                <v-row>
                                    <img v-if="twoFA.QRCode" :src="'data:image/png;base64, ' + twoFA.QRCode">
                                </v-row>
                            </v-container>
                        </v-card-text>
                        <v-card-actions class="mt-5">
                            <v-btn class="--cancel" color="blue darken-1" text @click="showQRCodeDialog = false">
                                Close
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <v-dialog
                    v-model="showDisable2FADialog"
                    content-class="account-modal"
                    max-width="450px"
                    transition="fade-transition"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" class="btn-danger rounded-xl ml-5" outlined large>
                            Disable
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title>
                            <span class="text">Disable two-factor authentication</span>
                        </v-card-title>
                        <v-card-text>
                            <v-container>
                                <v-row>
                                    <p class="description">
                                        Please enter your account password and 6-digit code to disable two-factor authentication
                                    </p>
                                    <input v-model="twoFA.passwordForDisabling" placeholder="Password"
                                           type="password"
                                    />
                                    <input v-model="twoFA.codeForDisabling" placeholder="6-digit code"
                                           type="password"
                                    />
                                </v-row>
                            </v-container>
                        </v-card-text>
                        <v-card-actions class="mt-5">
                            <v-btn class="--cancel" color="blue darken-1" text @click="showDisable2FADialog = false">
                                Cancel
                            </v-btn>
                            <v-btn :disabled="twoFA.passwordForDisabling == '' || twoFA.codeForDisabling == ''" class="--disable ml-"
                                   text
                                   @click="disable2FA"
                            >
                                Confirm
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </template>
        </div>

        <div v-if="step == 1">
            <span class="dashboard-subtitle mb-5">Two-factor authentication - STEP 1</span>

            <p class="dashboard-text">
                Click the button below to download recovery codes. Recovery codes are used to access your account in
                the event you cannot receive two factor authentication codes.
            </p>

            <p class="dashboard-text mb-10">
                <span class="color-red">IMPORTANT: </span>
                Download and print your recovery codes before continuing with two factor authentication setup.
            </p>

            <div class="codes-container">
                    <span v-for="code in twoFA.recoveryCodes" :key="code" class="code bold">
                        {{ code }}
                    </span>

                <div class="button-container mt-10">
                    <v-btn class="color-gray account-button rounded-xl mr-5" outlined large
                           @click="downloadRecoveryCodes"
                    >
                        Download
                    </v-btn>
                    <v-btn class="color-gray account-button rounded-xl" outlined large @click="copyRecoveryCodes">
                        Copy
                    </v-btn>
                </div>
            </div>

            <div class="button-container mt-10">
                <v-btn :disabled="!recoveryCodesDownloaded" class="color-gray account-button rounded-xl mr-5" dark
                       outlined large
                       @click="step = 2"
                >
                    Next
                </v-btn>
                <v-btn class="color-gray account-button rounded-xl" outlined large @click="cancel2FA">
                    Cancel
                </v-btn>
            </div>
        </div>

        <div v-if="step == 2">
            <span class="dashboard-subtitle mb-5">Two-factor authentication - STEP 2</span>
            <p class="dashboard-text mb-10">Scan the QR code below with the two factor authentication app on your
                phone.</p>
            <img :src="'data:image/png;base64, ' + twoFA.QRCode">

            <p class="dashboard-text mt-10">
                After scanning the barcode image the app will display a 6-digit code. Please enter it below.
            </p>

            <v-text-field v-model="twoFA.verifyCode" class="mb-5" dark label="Verify Code" />
            <v-btn :disabled="twoFA.verifyCode == ''" class="color-gray account-button rounded-xl" dark outlined
                   large
                   @click="enable2FA"
            >
                Enable
            </v-btn>
        </div>
    </div>
</template>

<script>
import * as rules from "vuelidate/lib/validators";
import CanMixin from "@/mixins/CanMixin";
import { mapState } from 'vuex'

export default {
    mixins: [CanMixin],

    data() {
        return {
            form: new Form(this, "form", {
                name: null,
                email: null,
                current_password: null,
                password: null,
                password_confirmation: null,
            }),

            twoFA: {
                QRCode: null,
                verifyCode: '',
                recoveryCodes: [],
                passwordForDisabling: '',
                codeForDisabling: '',
            },

            step: null,
            showQRCodeDialog: false,
            showDisable2FADialog: false,
            recoveryCodesDownloaded: false

        };
    },

    validations: {
        form: {
            name: {
                required: rules.required,
                minLength: rules.minLength(4),
                fullName: rules.helpers.regex("fullName", /^[a-zA-Z]{1,}( [a-zA-Z]{1,}){1,}$/),
            },
            email: { required: rules.required, email: rules.email },
            current_password: {
                requiredIf: rules.requiredIf(form => {
                    return form.password;
                }),
            },
            password: {
                minLength: rules.minLength(8),
                password: rules.helpers.regex("password", /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[*.!@#$%^&(){}[:;<>,.?~_/+=|]).{8,32}$/),
            },
            password_confirmation: {
                requiredIf: rules.requiredIf(form => {
                    return form.password;
                }),
                sameAs: rules.sameAs("password"),
            },
        },
    },

    computed: {
        ...mapState("User", ["user"]),
        is2FAEnabled() {
            return this.user.twoFA.enabled;
        }
    },

    methods: {
        async createSecret() {
            await this.$store.dispatch('Auth/generateSecret');
        },

        async createQRCode() {
            let response = await this.$store.dispatch('Auth/generateQRCode');
            this.twoFA.QRCode = response.data.qrcode_image;
        },

        async startEnabling2FA() {
            await this.createSecret();
            await this.createQRCode();
            await this.get2FARecoveryCodes();
            this.step = 1;
        },

        async enable2FA() {
            let response = await this.$store.dispatch('Auth/enable2FA', this.twoFA.verifyCode);

            if (response.status == 403) {
                this.$snackbar(`Incorrect two factor authentication code.`, "error");
            } else {
                this.$snackbar(`Two-factor authentication enabled successfully.`, "success");
                await this.$store.dispatch("User/fetchUser");
                this.step = null;
            }

            this.twoFA.verifyCode = '';
        },

        async disable2FA() {
            this.showDisable2FADialog = false;
            let response = await this.$store.dispatch('Auth/disable2FA', {
                password: this.twoFA.passwordForDisabling,
                code: this.twoFA.codeForDisabling
            })
            if (response.status !== 200) {
                this.$snackbar(`Password or code incorrect.`, "error");
            } else {
                this.$snackbar(`Two-factor authentication disabled successfully.`, "success");
                await this.$store.dispatch("User/fetchUser");
            }

            this.twoFA.passwordForDisabling = '';
            this.twoFA.codeForDisabling = '';
        },

        async get2FARecoveryCodes() {
            let response = await this.$store.dispatch('Auth/download2FARecoveryCodes');
            this.twoFA.recoveryCodes = response.data.codes;
        },

        copyRecoveryCodes() {
            navigator.clipboard.writeText(this.twoFA.recoveryCodes.join('\n'));
        },

        downloadRecoveryCodes() {
            let text = `fylm.ai recovery codes for ${this.user.email}\n\n`;
            text += this.twoFA.recoveryCodes.join('\n');
            let element = document.createElement("a");
            element.setAttribute("href", "data:text/plain;charset=utf-8," + encodeURIComponent(text));
            element.setAttribute("download", 'fylm - recovery codes');
            element.style.display = "none";
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);

            this.recoveryCodesDownloaded = true;
        },

        async recover2FA() {
            let response = await this.$store.dispatch('Auth/recovery', this.twoFA.recoveryCode);
        },

        cancel2FA() {
            this.step = null;
            this.twoFA.recoveryCodes = [];
            this.twoFA.verifyCode = '';
            this.twoFA.QRCode = null;
            this.recoveryCodesDownloaded = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.codes-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    background: #31313f;
    padding: 30px;
    min-width: 350px;
    width: 400px;
    grid-column-gap: 50px;

    .code {
        line-height: 1.5;

        &:before {
            content: "\00B7";
            font-weight: bold;
        }
    }

    .button-container {
        display: flex;
        grid-column: 1 / 3;
    }
}
</style>

<style lang="scss">
.account-modal {
    .v-card {
        padding: 20px 25px 25px;
        background: #1e1e1e;
        border-radius: 15px;

        .v-card__title,
        .v-card__text,
        .v-card__actions {
            padding: 0;
        }

        .v-card__title {
            font-weight: bold;
            color: white;
            margin-bottom: 30px;
            font-family: Poppins, sans-serif !important;
            line-height: 20px;
        }

        .v-card__text {
            input {
                background: white;
                border-radius: 5px;
                border: none;
                color: #121212;
                padding: 7px 10px;
                margin-bottom: 20px;
                font-size: 1rem;
                width: 100%;
                line-height: 20px;
                user-select: none;
                outline: none;
            }

            p {
                color: #989898 !important;
                text-align: center;
                font-size: 14px;
                line-height: 1.5;
            }
        }

        .v-card__actions {
            justify-content: center;

            .spacer {
                display: none;
            }

            button {
                border-radius: 15px;
                padding: 7px 40px;
                border: none;
                min-width: 125px;
                line-height: 20px;

                &.v-btn--disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                &.--cancel {
                    background: #989898;
                }

                &.--disable {
                    background: var(--color-danger);
                }

                .v-btn__content {
                    text-transform: uppercase;
                    font-size: 1rem;
                    color: white;
                }
            }
        }
    }
}
</style>
